/**
 * Swiss UI primitives — white/red/black design language.
 * Per /app/design_guidelines.json
 *
 * Use these wherever possible in admin tab components so the visual
 * language stays consistent without scattering one-off Tailwind chains.
 */
import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
// ── KPI Card ──
export const KpiCard = ({ label, value, delta, deltaLabel, icon: Icon, onClick, tone = 'default', testid }) => {
  const deltaIsPositive = typeof delta === 'number' ? delta >= 0 : false;
  const toneClass = {
    default: 'text-[#0A0A0A]',
    danger: 'text-[#E52B20]',
    success: 'text-[#039855]',
    warn: 'text-[#F79009]',
  }[tone] || 'text-[#0A0A0A]';

  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={`text-left p-4 bg-white border border-[#E5E5E5] flex flex-col gap-2 transition-all ${onClick ? 'hover:border-[#0A0A0A] hover:shadow-[4px_4px_0px_#0A0A0A] cursor-pointer' : ''}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold">{label}</p>
        {Icon && <Icon className="h-3.5 w-3.5 text-[#666]" strokeWidth={2} />}
      </div>
      <p className={`text-3xl md:text-4xl font-black tracking-tight ${toneClass}`}>{value}</p>
      {delta !== undefined && delta !== null && (
        <div className={`inline-flex items-center gap-1 text-xs font-bold ${deltaIsPositive ? 'text-[#039855]' : 'text-[#E52B20]'}`}>
          {deltaIsPositive ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {Math.abs(delta)}%{deltaLabel ? ` ${deltaLabel}` : ''}
        </div>
      )}
    </Wrapper>
  );
};

// ── Status Pill ──
export const StatusPill = ({ children, variant = 'pending', testid }) => {
  const styles = {
    active:    'bg-[#0A0A0A] text-white border-[#0A0A0A]',
    pending:   'bg-white text-[#0A0A0A] border-[#E5E5E5]',
    alert:     'bg-[#E52B20] text-white border-[#E52B20]',
    success:   'bg-[#039855] text-white border-[#039855]',
    warn:      'bg-[#F79009] text-white border-[#F79009]',
    muted:     'bg-[#F9F9FA] text-[#666] border-[#E5E5E5]',
  }[variant] || 'bg-white text-[#0A0A0A] border-[#E5E5E5]';

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] border ${styles}`}
      data-testid={testid}
    >
      {children}
    </span>
  );
};

// ── Section Header ──
export const SectionHeader = ({ overline, title, action, testid }) => (
  <div className="flex items-end justify-between gap-3 mb-4" data-testid={testid}>
    <div>
      {overline && <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold">{overline}</p>}
      <h2 className="text-xl md:text-2xl font-black tracking-tight">{title}</h2>
    </div>
    {action && <div>{action}</div>}
  </div>
);

// ── Page Header (top of every tab) ──
export const PageHeader = ({ overline, title, subtitle, actions, testid }) => (
  <div className="mb-6" data-testid={testid}>
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        {overline && <p className="text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold mb-1">{overline}</p>}
        <h1 className="text-2xl md:text-3xl font-black tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-[#666] mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  </div>
);

// ── Empty State ──
export const EmptyState = ({ icon: Icon, title, description, action, testid }) => (
  <div className="bg-white border border-[#E5E5E5] py-12 px-6 text-center" data-testid={testid}>
    {Icon && <Icon className="h-10 w-10 text-[#E5E5E5] mx-auto mb-4" strokeWidth={1.5} />}
    <h3 className="text-base font-black">{title}</h3>
    {description && <p className="text-sm text-[#666] mt-1 max-w-md mx-auto">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

// ── Swiss Button ──
export const SwissButton = ({ variant = 'primary', size = 'md', children, className = '', ...props }) => {
  const variants = {
    primary:    'bg-[#E52B20] text-white border-[#E52B20] hover:bg-[#C92218] hover:border-[#C92218]',
    secondary:  'bg-white text-[#0A0A0A] border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white',
    ghost:      'bg-transparent text-[#0A0A0A] border-transparent hover:bg-[#F9F9FA] hover:border-[#E5E5E5]',
    outline:    'bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#0A0A0A]',
    danger:     'bg-white text-[#E52B20] border-[#E52B20] hover:bg-[#E52B20] hover:text-white',
  };
  const sizes = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-9 px-4 text-xs',
    lg: 'h-11 px-5 text-sm',
  };
  return (
    <button
      className={`inline-flex items-center gap-1.5 border font-bold uppercase tracking-wider transition-colors ${variants[variant]} ${sizes[size]} disabled:opacity-50 disabled:pointer-events-none ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

// ── DataTable wrapper (renders a flat 1-px border table) ──
export const SwissTable = ({ children, className = '' }) => (
  <div className={`bg-white border border-[#E5E5E5] overflow-x-auto ${className}`}>
    <table className="w-full border-collapse text-sm text-left">
      {children}
    </table>
  </div>
);

export const SwissTH = ({ children, className = '' }) => (
  <th className={`text-left px-3 py-2 font-bold text-[10px] uppercase tracking-[0.15em] text-[#666] border-b-2 border-[#0A0A0A] bg-white ${className}`}>
    {children}
  </th>
);

export const SwissTD = ({ children, className = '', ...rest }) => (
  <td className={`px-3 py-2.5 border-b border-[#E5E5E5] text-sm ${className}`} {...rest}>
    {children}
  </td>
);

// ── Card (generic Swiss surface) ──
export const SwissCard = ({ children, className = '', testid, ...props }) => (
  <div
    className={`bg-white border border-[#E5E5E5] ${className}`}
    data-testid={testid}
    {...props}
  >
    {children}
  </div>
);

// ── Inline number editor — click value → swap to input → blur commits ──
export const InlineNumber = ({ value, onChange, prefix = '', suffix = '', step = 1, min, max, testid }) => {
  const [editing, setEditing] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(value);

  React.useEffect(() => { setLocalValue(value); }, [value]);

  if (editing) {
    return (
      <input
        type="number"
        autoFocus
        value={localValue ?? ''}
        step={step}
        min={min}
        max={max}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={() => {
          const n = Number(localValue);
          if (!Number.isNaN(n) && n !== value) onChange(n);
          setEditing(false);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setLocalValue(value); setEditing(false); } }}
        onClick={(e) => e.stopPropagation()}
        className="border border-[#0A0A0A] bg-white px-2 py-0.5 text-sm font-mono font-bold focus:outline-none w-20 tabular-nums"
        data-testid={testid ? `${testid}-input` : undefined}
      />
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] px-1 -mx-1 hover:bg-[#F9F9FA] inline-flex items-center gap-0.5 text-sm font-mono font-bold tabular-nums"
      title="Click to edit"
      data-testid={testid}
    >
      {prefix && <span className="text-[#666]">{prefix}</span>}
      {value ?? '—'}
      {suffix && <span className="text-[#666] text-xs">{suffix}</span>}
    </button>
  );
};

// ── Tonal labels ──
export const Overline = ({ children, className = '' }) => (
  <p className={`text-[10px] uppercase tracking-[0.2em] text-[#666] font-bold ${className}`}>{children}</p>
);

// ── Inline editable cell — generic dropdown swap ──
// Click trigger → swaps to native <select> → blur commits via onChange
export const InlineSelect = ({ value, options, onChange, renderTrigger, testid }) => {
  const [editing, setEditing] = React.useState(false);

  if (editing) {
    return (
      <select
        autoFocus
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setEditing(false); }}
        onBlur={() => setEditing(false)}
        onClick={(e) => e.stopPropagation()}
        className="border border-[#0A0A0A] bg-white px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold focus:outline-none"
        data-testid={testid ? `${testid}-select` : undefined}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  return (
    <button
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className="cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
      title="Click to edit"
      data-testid={testid}
    >
      {renderTrigger ? renderTrigger(value) : <StatusPill variant="pending">{value || '—'}</StatusPill>}
    </button>
  );
};

// ── Status pill variants mapped to common booking/request status keys ──
export const STATUS_VARIANT_MAP = {
  // common workflow statuses
  upcoming:  'pending',
  active:    'active',
  in_progress: 'active',
  completed: 'success',
  done:      'success',
  approved:  'success',
  paid:      'success',
  pending:   'pending',
  new:       'alert',
  in_review: 'warn',
  declined:  'alert',
  cancelled: 'alert',
  void:      'muted',
  checked_out: 'active',
  scheduled: 'pending',
  overdue:   'alert',
  draft:     'muted',
  signed:    'success',
  converted: 'success',
};

export const StatusPillAuto = ({ status, testid }) => {
  const variant = STATUS_VARIANT_MAP[(status || '').toLowerCase()] || 'muted';
  return <StatusPill variant={variant} testid={testid}>{status || '—'}</StatusPill>;
};
